import { useCallback, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { AppWindow, CalendarClock, CircleArrowUp, Clock, LayoutPanelLeft } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
  Button,
} from '@scio3/ui';
import { PLAN_FEATURES } from '@scio3/shared-types';
import { ObjectChip } from '@/components/shared/ObjectChip';
import {
  useBillingDialogStore,
  openUpgradeWizard,
  UPGRADE_ITEM_KINDS,
  type UpgradeItemKind,
  type UpgradeRequiredAsset,
} from '../stores/billing-dialog-store';
import { getPlanDisplayName, PLAN_TIER_ORDER } from '../utils/plan-helpers';

/** The type mark on each refused item's chip — the same icons the asset-usage chips use. */
const KIND_ICONS: Record<UpgradeItemKind, typeof AppWindow> = {
  [UPGRADE_ITEM_KINDS.APP]: AppWindow,
  [UPGRADE_ITEM_KINDS.SCHEDULE]: CalendarClock,
  [UPGRADE_ITEM_KINDS.SPLIT_SCREEN]: LayoutPanelLeft,
  [UPGRADE_ITEM_KINDS.FEATURE]: Clock,
};

// Default monthly USD price per device for the user's current plan — matches
// scio1's currentDeviceProduct.plans.usd.monthly.price for the default product
// shown in engagePlanConfirmModal.
const PLAN_PRICE_USD_MONTHLY: Record<string, number> = {
  free: 0,
  standard: 10,
  pro: 12.5,
  proPlus: 15,
  engage: 30,
};

/** Chips shown before the refused-item list collapses behind "+N more". */
const COLLAPSED_CHIPS = 8;

export function UpgradeRequiredDialog() {
  const { t } = useTranslation();
  const activeDialog = useBillingDialogStore((s) => s.activeDialog);
  const dialogContext = useBillingDialogStore((s) => s.dialogContext);
  const closeDialog = useBillingDialogStore((s) => s.closeDialog);

  const minPlanRequired = (dialogContext.minPlanRequired as string) ?? '';
  const currentPlan = (dialogContext.currentPlan as string) ?? '';
  const assets = (dialogContext.assets as UpgradeRequiredAsset[] | undefined) ?? [];
  const title = (dialogContext.title as string) ?? t('Upgrade Required');
  const customSubtitle = dialogContext.subtitle as string | undefined;
  // SCIO1 parity (#1181): when backend returns the affected assets, show the
  // "Your current plan - X - doesn't support these features" message with the
  // asset list — matches scio1 engagePlanConfirmModal. Falls back to the
  // generic plan-required line when no asset list is provided.
  const showAssetList = assets.length > 0 && currentPlan;
  const currentPlanName = currentPlan ? getPlanDisplayName(currentPlan) : '';
  const currentPlanPrice = PLAN_PRICE_USD_MONTHLY[currentPlan] ?? 0;
  const isEngageUpgrade = minPlanRequired === PLAN_FEATURES.ENGAGE;
  // `minPlanRequired` is only a real plan name for plan-tier gates (e.g. proPlus).
  // Some backend gates return a feature key like `appMeta` instead of a plan tier;
  // rendering it verbatim leaks "requires the appMeta plan or higher" to the UI (#1354).
  // For those, fall back to the current-plan message (scio1 parity / Image 2).
  const isKnownPlanTier = PLAN_TIER_ORDER.includes(
    minPlanRequired as (typeof PLAN_TIER_ORDER)[number],
  );
  const fallbackSubtitle =
    customSubtitle ??
    (isKnownPlanTier
      ? t('This feature requires the {{plan}} plan or higher', {
          plan: getPlanDisplayName(minPlanRequired),
        })
      : currentPlanName
        ? t(
            "Your current plan - {{plan}} - doesn't support this feature. Upgrade now to unlock it.",
            { plan: currentPlanName },
          )
        : t("Your current plan doesn't support this feature. Upgrade now to unlock it."));

  const handleUpgrade = useCallback(() => {
    openUpgradeWizard(minPlanRequired);
  }, [minPlanRequired]);

  // A long refusal (a playlist full of locked apps) shows its first few chips and a "+N more"
  // chip, so the sentence and the buttons stay in view instead of sitting below a wall of names.
  // The toggle counts as a chip: hiding only one would trade a name for a button that says 1.
  const [isListExpanded, setIsListExpanded] = useState(false);
  const upgradeButtonRef = useRef<HTMLButtonElement>(null);
  const isCollapsible = assets.length > COLLAPSED_CHIPS + 1;
  const visibleAssets =
    isCollapsible && !isListExpanded ? assets.slice(0, COLLAPSED_CHIPS) : assets;
  const hiddenCount = isCollapsible ? assets.length - COLLAPSED_CHIPS : 0;

  const isOpen = activeDialog === 'upgradeRequired';
  // Every open starts collapsed. Reset on ANY close — Update Subscription swaps the store's dialog
  // without going through onOpenChange — by adjusting state during render, not in an effect.
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (wasOpen !== isOpen) {
    setWasOpen(isOpen);
    if (!isOpen) setIsListExpanded(false);
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeDialog()}>
      <DialogContent
        className="max-h-[85vh] flex flex-col overflow-hidden sm:max-w-md"
        // Radix focuses the first focusable element on open, which on a long list is the
        // "+N more" row. Open on the action this popup exists for instead.
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          upgradeButtonRef.current?.focus();
        }}
        data-testid="upgrade-required-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {/* The upgrade mark, not a warning triangle and not sparkles: a refusal that can be
                paid away is a step up, and the arrow is the shape people already read as one.
                Brand green, because it introduces the action the green button below performs —
                a severity colour would file a sales prompt as a notice.
                primary-dark, not primary: #1ab394 is 2.65:1 on the white dialog, under even the
                3:1 bar for a graphic. The dark brand green is 8.14:1. Dark mode keeps the light
                token, which is what carries against a near-black surface. */}
            <CircleArrowUp className="h-5 w-5 text-primary-dark dark:text-primary" />
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4 space-y-3">
          {showAssetList ? (
            <>
              {isEngageUpgrade ? (
                <p className="text-sm">
                  {t('Engage license is required for the following interactive asset(s):')}
                </p>
              ) : (
                <p className="text-sm">
                  {/* One whole sentence per count, not production's "Your current plan - Free -
                      doesn't…" assembled from two keys around the plan: the dashes read as an
                      unfilled template, and a split sentence cannot be reordered in translation.
                      Same phrasing as the plan banner ("Your Free Plan doesn't include…").
                      The plan is bold through a named tag, so it stays one translatable sentence. */}
                  <Trans
                    i18nKey={
                      assets.length === 1
                        ? "Your <planName>{{plan}} Plan</planName> doesn't support this feature."
                        : "Your <planName>{{plan}} Plan</planName> doesn't support these features."
                    }
                    values={{ plan: currentPlanName }}
                    components={{ planName: <strong className="font-semibold" /> }}
                  />
                </p>
              )}
              {/* One chip per refused item, wrapping onto as many lines as it needs — the same
                  ObjectChip the Move to Trash warning names an asset's playlists and screens with,
                  so a named object looks the same in both places. Its type icon says what it is
                  (an app's content, a schedule, a split screen, a feature); the app follows as a
                  quieter label. With no name, the app stands in as the name. */}
              <ul className="flex flex-wrap gap-1.5">
                {visibleAssets.map((asset, i) => {
                  const Icon = KIND_ICONS[asset.kind ?? UPGRADE_ITEM_KINDS.APP];
                  return (
                    <li key={asset.id ?? i} className="max-w-full">
                      <ObjectChip
                        icon={<Icon className="h-3 w-3 shrink-0 text-muted-foreground" />}
                        label={asset.name || asset.appName || ''}
                        detail={asset.name ? asset.appName : undefined}
                      />
                    </li>
                  );
                })}
                {hiddenCount > 0 && (
                  <li>
                    <Button
                      variant="ghost"
                      size="xs"
                      className="cursor-pointer rounded-full px-2.5 text-muted-foreground"
                      onClick={() => setIsListExpanded((v) => !v)}
                      data-testid="upgrade-required-list-toggle"
                    >
                      {isListExpanded
                        ? t('Show less')
                        : t('+{{count}} more', { count: hiddenCount })}
                    </Button>
                  </li>
                )}
              </ul>
              {isEngageUpgrade && (
                <div className="space-y-2 pt-2">
                  <p className="text-sm font-semibold">{t('Important')}:</p>
                  <p className="text-sm">
                    {t('Interactive Engage Assets requires Engage license plan:')} $30/mo/device.
                  </p>
                  <p className="text-sm">
                    {t('You can review full')}{' '}
                    <a
                      href="https://www.optisigns.com/pricing"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline hover:text-primary/80"
                    >
                      {t('pricing plans comparison here.')}
                    </a>
                  </p>
                  <p className="text-sm">
                    {t('Your current plan is {{plan}} ${{price}}/mo/device', {
                      plan: currentPlanName,
                      price: currentPlanPrice,
                    })}
                  </p>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">{fallbackSubtitle}</p>
          )}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" className="cursor-pointer">
              {t('Cancel')}
            </Button>
          </DialogClose>
          <Button ref={upgradeButtonRef} onClick={handleUpgrade} className="cursor-pointer">
            {showAssetList ? t('Update Subscription') : t('Upgrade')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
